/**
 * Archived work, and the route back out of it.
 *
 * @module
 */
import { useThreadActions } from "../../hooks/useThreadActions";
import { readLocalApi } from "../../localApi";
import {
  selectProjectsAcrossEnvironments,
  selectThreadShellsAcrossEnvironments,
} from "../../store";
import { useEnvironmentStoreSnapshot } from "../../hooks/useEnvironmentStoreSnapshot";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { ProjectFavicon } from "../ProjectFavicon";
import { useProjectDisplayNameResolver } from "../projects/useProjectDisplayNameResolver";
import { Button } from "../ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../ui/empty";
import { toastManager } from "../ui/toast";
import { groupArchivedCmmdTasks, groupArchivedThreads } from "./ArchivedThreadsPanel.logic";
import { SettingsSection } from "./settingsLayout";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { ScopedThreadRef } from "@cmmd-forge/contracts";
import { ArchiveIcon, ArchiveX } from "lucide-react";
import { ReactElement, useCallback, useMemo } from "react";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";

function useArchivedThreadSections(): readonly ReactElement[] {
  const storeSnapshot = useEnvironmentStoreSnapshot();
  const projects = useMemo(() => selectProjectsAcrossEnvironments(storeSnapshot), [storeSnapshot]);
  const threads = useMemo(
    () => selectThreadShellsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );
  const { unarchiveThread, confirmAndDeleteThread } = useThreadActions();
  const resolveProjectDisplayName = useProjectDisplayNameResolver();
  const archivedGroups = useMemo(
    () =>
      groupArchivedThreads(projects, threads).map((group) => {
        const sorted = group.threads.toSorted((left, right) => {
          const leftKey = left.archivedAt ?? left.createdAt;
          const rightKey = right.archivedAt ?? right.createdAt;
          return rightKey.localeCompare(leftKey) || right.id.localeCompare(left.id);
        });
        return { project: group.project, threads: sorted };
      }),
    [projects, threads],
  );

  const handleArchivedThreadContextMenu = useCallback(
    async (threadRef: ScopedThreadRef, position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const clicked = await api.contextMenu.show(
        [
          { id: "unarchive", label: "Unarchive" },
          { id: "delete", label: "Delete", destructive: true },
        ],
        position,
      );

      if (clicked === "unarchive") {
        try {
          await unarchiveThread(threadRef);
        } catch (error) {
          toastManager.add({
            type: "error",
            title: "Failed to unarchive thread",
            description: error instanceof Error ? error.message : "An error occurred.",
          });
        }
        return;
      }

      if (clicked === "delete") {
        await confirmAndDeleteThread(threadRef);
      }
    },
    [confirmAndDeleteThread, unarchiveThread],
  );

  return archivedGroups.map(({ project, threads: projectThreads }) => {
    // A linked CMMD project's checkout name is a slug of its board title —
    // prefer the name chosen in Forge for the section header, same as every
    // other project label.
    const projectName = project
      ? (resolveProjectDisplayName(project.environmentId, project.id) ?? project.name)
      : null;
    return (
      <SettingsSection
        key={`thread:${project?.id ?? "__orphan__"}`}
        title={projectName ?? "Archived threads"}
        icon={
          project ? (
            <ProjectFavicon
              environmentId={project.environmentId}
              cwd={project.cwd}
              projectName={projectName ?? project.name}
            />
          ) : undefined
        }
      >
        {projectThreads.map((thread) => (
          <div
            key={thread.id}
            className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 first:border-t-0 sm:px-5"
            onContextMenu={(event) => {
              event.preventDefault();
              void handleArchivedThreadContextMenu(
                scopeThreadRef(thread.environmentId, thread.id),
                {
                  x: event.clientX,
                  y: event.clientY,
                },
              );
            }}
          >
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-medium text-foreground">{thread.title}</h3>
              <p className="text-xs text-muted-foreground">
                Archived {formatRelativeTimeLabel(thread.archivedAt ?? thread.createdAt)}
                {" · Created "}
                {formatRelativeTimeLabel(thread.createdAt)}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 cursor-pointer gap-1.5 px-2.5"
              onClick={() =>
                void unarchiveThread(scopeThreadRef(thread.environmentId, thread.id)).catch(
                  (error) => {
                    toastManager.add({
                      type: "error",
                      title: "Failed to unarchive thread",
                      description: error instanceof Error ? error.message : "An error occurred.",
                    });
                  },
                )
              }
            >
              <ArchiveX className="size-3.5" />
              <span>Unarchive</span>
            </Button>
          </div>
        ))}
      </SettingsSection>
    );
  });
}

// ===========================================================================
// Phase 2: New IA panels. Existing panels above are preserved for backward
// compat with /settings/general, /settings/chat, /settings/editor, etc. New
// panels carry the migrated content for the redesigned sidebar groups.
// ===========================================================================

function useArchivedCmmdTaskSections(): readonly ReactElement[] {
  const { tasks: cmmdTasks, projects: cmmdProjects } = useCmmdWorkIntakeSnapshot();

  const groups = useMemo(
    () => groupArchivedCmmdTasks(cmmdProjects, cmmdTasks),
    [cmmdProjects, cmmdTasks],
  );

  return groups.map((group) => (
    <SettingsSection key={`cmmd-task:${group.projectId ?? "__no-project__"}`} title={group.title}>
      {group.tasks.map((task) => (
        <div
          key={task.id}
          className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 first:border-t-0 sm:px-5"
        >
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-medium text-foreground">{task.title}</h3>
            <p className="text-xs text-muted-foreground">
              Archived {formatRelativeTimeLabel(task.archivedAt!)}
              {task.status ? ` · ${task.status.replace("_", " ")}` : ""}
            </p>
          </div>
        </div>
      ))}
    </SettingsSection>
  ));
}

/**
 * Archived Forge thread sections, newest archive first, grouped by project.
 *
 * Runs on every surface: the store, `useThreadActions`, and the orphan group in
 * `groupArchivedThreads` are all surface-agnostic. Only the native context menu
 * is desktop-only — `readLocalApi()` returns null in the browser, where the
 * Unarchive button carries the same action.
 */

export function ArchivedSection() {
  const threadSections = useArchivedThreadSections();
  const taskSections = useArchivedCmmdTaskSections();

  if (threadSections.length === 0 && taskSections.length === 0) {
    return (
      <SettingsSection id="archived" title="Archived">
        <Empty className="min-h-88">
          <EmptyMedia variant="icon">
            <ArchiveIcon />
          </EmptyMedia>
          <EmptyHeader>
            <EmptyTitle>Nothing archived yet</EmptyTitle>
            <EmptyDescription>Archived threads and tasks will appear here.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </SettingsSection>
    );
  }

  return (
    <>
      {threadSections}
      {taskSections}
    </>
  );
}

/** Archived CMMD task sections, or an empty list when CMMD has nothing archived. */
